import CenteredLayout from "../features/layout/Centered"
import { useCMSReady } from "../domain/CMS/useCMSReady"
import appInsightsLogger from "../features/logging/appInsightsLogger"
import { useEffect, useState, useCallback } from "react"
import { selectUser } from "../features/shared/selectors"
import { appInsights } from "../features/logging/ApplicationInsightsService"
import { DataStatus } from "../domain/enums"
import { getBaseEndpoint } from "../features/endpoints/endpointManager"
import { getData } from "../features/shared/apiService"
import { useSelector } from "react-redux"
import Spinner from "../features/shared/components/Spinner"
import {
  Typography,
  typographyTypes,
} from "../features/shared/components/Typography"
import SkeletonLoader from "../features/shared/components/SkeletonLoader"
import Button from "../features/shared/components/Button"

const BASE_API_ENDPOINT = getBaseEndpoint()
const SERVICES: {
  name: string
  cmsKey: string
  url: string
}[] = [
  {
    name: "Account",
    cmsKey: "accountServiceLabel",
    url: `${BASE_API_ENDPOINT}/account/healthz`,
  },
  {
    name: "eSign",
    cmsKey: "esignServiceLabel",
    url: `${BASE_API_ENDPOINT}/esign/healthz`,
  },
  {
    name: "Fileupload",
    cmsKey: "fileuploadServiceLabel",
    url: `${BASE_API_ENDPOINT}/files/healthz`,
  },
  {
    name: "Payment",
    cmsKey: "paymentServiceLabel",
    url: `${BASE_API_ENDPOINT}/payment/healthz`,
  },
  {
    name: "Practice",
    cmsKey: "practiceServiceLabel",
    url: `${BASE_API_ENDPOINT}/practice/healthz`,
  },
  {
    name: "Provider",
    cmsKey: "providerServiceLabel",
    url: `${BASE_API_ENDPOINT}/provider/healthz`,
  },
  {
    name: "Rate",
    cmsKey: "rateServiceLabel",
    url: `${BASE_API_ENDPOINT}/rate/healthz`,
  },
  {
    name: "Referral",
    cmsKey: "referralServiceLabel",
    url: `${BASE_API_ENDPOINT}/referral/healthz`,
  },
  {
    name: "Search",
    cmsKey: "searchServiceLabel",
    url: `${BASE_API_ENDPOINT}/search/healthz`,
  },
  {
    name: "Tin-validation",
    cmsKey: "tinValidationServiceLabel",
    url: `${BASE_API_ENDPOINT}/tin-validation/healthz`,
  },
]

interface AboutProps {
  onClose: () => void
}

type HealthResponse = {
  Version: string
  Status: string
}

type ServiceResult = {
  version: string
  status: DataStatus
}

export const About = ({ onClose }: AboutProps) => {
  // Use useCMSReady for CMS loading state
  const {
    cms: cmsContent,
    isLoading,
    isReady,
    hasError,
  } = useCMSReady(["about"])
  const aboutText = cmsContent.about
  const user = useSelector(selectUser)

  // App/session/user info
  const currentVersion = __APP_VERSION__
  const sessionId = `${appInsights.context.sessionManager.automaticSession.id}`
  const userGuid = `${user?.id ?? "N/A"}`

  // Service health state
  const [results, setResults] = useState<{ [key: string]: ServiceResult }>({})
  const [showTable, setShowTable] = useState(false)

  const fetchServiceVersions = useCallback(async () => {
    await Promise.all(
      SERVICES.map(async (service) => {
        try {
          setResults((prev) => ({
            ...prev,
            [service.name]: {
              version: "",
              status: DataStatus.LOADING,
            },
          }))

          const data = await getData<HealthResponse>(service.url)
          if (!data) {
            throw new Error(
              `Failed to fetch health for ${service.name} service`,
            )
          }

          setResults((prev) => ({
            ...prev,
            [service.name]: {
              version: data.Version,
              status: DataStatus.LOADED,
            },
          }))
        } catch (e) {
          appInsightsLogger.traceError(
            `Error fetching service version for ${service.name}: ${e}`,
          )
          setResults((prev) => ({
            ...prev,
            [service.name]: {
              version: "unknown",
              status: DataStatus.ERROR,
            },
          }))
        }
      }),
    )
  }, [])

  useEffect(() => {
    if (showTable) {
      fetchServiceVersions()
    } else {
      setResults({})
    }
  }, [showTable, fetchServiceVersions])
  if (isLoading) {
    return (
      <CenteredLayout>
        <Spinner />
      </CenteredLayout>
    )
  } else if (hasError || !isReady) {
    const errorMessage = `About -- Error loading CMS content`
    appInsightsLogger.traceError(errorMessage)
    throw new Error(errorMessage)
  }
  return (
    <CenteredLayout>
      <div className="bg-white px-[10px] min-w-[448px]">
        <div className="flex flex-col flex-start gap-6 pt-[30px] pb-7">
          <div className="flex flex-row gap-1">
            <Typography
              variant={typographyTypes.BANNER_TITLE}
              styleOverrides="mb-2 text-gray-700"
              text={aboutText!.currentVersionLabel}
            />
            <Typography
              variant={typographyTypes.BANNER_TITLE}
              styleOverrides="mb-2 text-gray-700 font-normal"
              text={currentVersion}
            />
          </div>
          <div className="flex flex-row gap-1">
            <Typography
              variant={typographyTypes.BANNER_TITLE}
              styleOverrides="mb-2 text-gray-700"
              text={aboutText!.userSessionIdLabel}
            />
            <Typography
              variant={typographyTypes.BANNER_TITLE}
              styleOverrides="mb-2 text-gray-700 font-normal"
              text={sessionId}
            />
          </div>
        </div>
        {showTable && (
          <div className="flex flex-col gap-3 mb-16 pt-[24px]">
            <div className="flex flex-row gap-1">
              <Typography
                variant={typographyTypes.BANNER_TITLE}
                styleOverrides="text-gray-700"
                text={aboutText!.userGuidLabel}
              />
              <Typography
                variant={typographyTypes.BANNER_TITLE}
                styleOverrides="text-gray-700 font-normal"
                text={userGuid}
              />
            </div>
            {SERVICES.map((service) => (
              <div key={service.name} className="flex items-center">
                <Typography
                  variant={typographyTypes.BANNER_TITLE}
                  styleOverrides="text-gray-700"
                  text={
                    aboutText![service.cmsKey as keyof typeof aboutText] ||
                    `${service.name}:`
                  }
                />
                {!results[service.name]?.status ||
                results[service.name]?.status === DataStatus.LOADING ? (
                  <SkeletonLoader className="ml-1 !w-[64px]" />
                ) : (
                  <Typography
                    variant={typographyTypes.BANNER_TITLE}
                    styleOverrides="ml-1 text-gray-700 font-normal"
                    text={results[service.name]?.version || "[#.#.#]"}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-row justify-end gap-4">
          <Button
            styleOverrides="px-4 py-2"
            onClick={onClose}
            buttonLabel={cmsContent.common?.closeModalButtonText}
            type="transparent"
            roundedFull={false}
          />
          <Button
            styleOverrides="px-4 py-2"
            buttonLabel={
              showTable
                ? aboutText!.basicInformationButtonLabel
                : aboutText!.moreInformationButtonLabel
            }
            roundedFull={false}
            type="primary"
            onClick={() => setShowTable((prev) => !prev)}
          />
        </div>
      </div>
    </CenteredLayout>
  )
}

export default About
